/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { IconButton, Flex, Group } from '@chakra-ui/react'
import { $isCodeHighlightNode } from '@lexical/code'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createQuoteNode } from '@lexical/rich-text'
import { $wrapNodes } from '@lexical/selection'
import { mergeRegister } from '@lexical/utils'
import type { LexicalEditor, TextFormatType } from 'lexical'
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND
} from 'lexical'
import type { JSX } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AiOutlineOrderedList, AiOutlineUnorderedList } from 'react-icons/ai'
import { FiBold, FiItalic, FiCode } from 'react-icons/fi'
import { MdFormatQuote } from 'react-icons/md'

import { getDOMRangeRect } from './getDOMRangeRect'
import { getSelectedNode } from './getSelectedNode'
import { setFloatingElemPosition } from './setFloatingElemPosition'

function TextFormatFloatingToolbar({
  editor,
  anchorElem,
  isBold,
  isItalic,
  isCode,
  isOrderedList,
  isUnorderedList,
  isQuote
}: {
  editor: LexicalEditor
  anchorElem: HTMLElement
  isBold: boolean
  isCode: boolean
  isItalic: boolean
  isOrderedList: boolean
  isUnorderedList: boolean
  isQuote: boolean
}): JSX.Element {
  const popupCharStylesEditorRef = useRef<HTMLDivElement | null>(null)

  function mouseMoveListener(e: MouseEvent) {
    if (popupCharStylesEditorRef?.current && (e.buttons === 1 || e.buttons === 3)) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'none') {
        const x = e.clientX
        const y = e.clientY
        const elementUnderMouse = document.elementFromPoint(x, y)

        if (!popupCharStylesEditorRef.current.contains(elementUnderMouse)) {
          // Mouse is not over the target element => not a normal click, but probably a drag
          popupCharStylesEditorRef.current.style.pointerEvents = 'none'
        }
      }
    }
  }

  function mouseUpListener(_e: MouseEvent) {
    if (popupCharStylesEditorRef?.current) {
      if (popupCharStylesEditorRef.current.style.pointerEvents !== 'auto') {
        popupCharStylesEditorRef.current.style.pointerEvents = 'auto'
      }
    }
  }

  useEffect(() => {
    if (popupCharStylesEditorRef?.current) {
      document.addEventListener('mousemove', mouseMoveListener)
      document.addEventListener('mouseup', mouseUpListener)

      return () => {
        document.removeEventListener('mousemove', mouseMoveListener)
        document.removeEventListener('mouseup', mouseUpListener)
      }
    }

    return undefined
  }, [popupCharStylesEditorRef])

  const updateTextFormatFloatingToolbar = useCallback(() => {
    const selection = $getSelection()

    const popupCharStylesEditorElem = popupCharStylesEditorRef.current
    const nativeSelection = window.getSelection()

    if (popupCharStylesEditorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()
    if (
      selection !== null &&
      nativeSelection !== null &&
      !nativeSelection.isCollapsed &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const rangeRect = getDOMRangeRect(nativeSelection, rootElement)

      setFloatingElemPosition(rangeRect, popupCharStylesEditorElem, anchorElem)
    }
  }, [editor, anchorElem])

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement

    const update = () => {
      editor.getEditorState().read(() => {
        updateTextFormatFloatingToolbar()
      })
    }

    window.addEventListener('resize', update)
    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)
      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update)
      }
    }
  }, [editor, updateTextFormatFloatingToolbar, anchorElem])

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateTextFormatFloatingToolbar()
    })
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateTextFormatFloatingToolbar()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTextFormatFloatingToolbar()
          return false
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, updateTextFormatFloatingToolbar])

  return (
    <Flex
      ref={popupCharStylesEditorRef}
      pos="absolute"
      zIndex="popover"
      top={0}
      left={0}
      verticalAlign="middle"
      p={1}
      bg="bg"
      opacity={0}
      borderRadius="sm"
      shadow="0 5px 10px rgba(0, 0, 0, 0.3)"
      transition="opacity 0.3s ease-in-out"
      willChange="transform"
    >
      {editor.isEditable() && (
        <Group colorPalette="gray">
          <IconButton
            aria-label="Format text as italic"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')
            }}
            size="xs"
            variant={isItalic ? 'solid' : 'ghost'}
          >
            <FiItalic />
          </IconButton>
          <IconButton
            aria-label="Format text as bold"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')
            }}
            size="xs"
            variant={isBold ? 'solid' : 'ghost'}
          >
            <FiBold />
          </IconButton>
          <IconButton
            aria-label="Format text to unordered list"
            onClick={() => {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
            }}
            size="xs"
            variant={isUnorderedList ? 'solid' : 'ghost'}
          >
            <AiOutlineUnorderedList />
          </IconButton>
          <IconButton
            aria-label="Format text to ordered list"
            onClick={() => {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
            }}
            size="xs"
            variant={isOrderedList ? 'solid' : 'ghost'}
          >
            <AiOutlineOrderedList />
          </IconButton>
          <IconButton
            aria-label="Insert quote block"
            onClick={() => {
              editor.update(() => {
                const selection = $getSelection()

                if ($isRangeSelection(selection)) {
                  $wrapNodes(selection, () => $createQuoteNode())
                }
              })
              // }
            }}
            size="xs"
            variant={isQuote ? 'solid' : 'ghost'}
          >
            <MdFormatQuote />
          </IconButton>
          <IconButton
            aria-label="Insert code block"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')
            }}
            size="xs"
            variant={isCode ? 'solid' : 'ghost'}
          >
            <FiCode />
          </IconButton>
        </Group>
      )}
    </Flex>
  )
}

function useFloatingTextFormatToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement
): ReturnType<typeof createPortal> | null {
  const [isText, setIsText] = useState(false)
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isCode, setIsCode] = useState(false)
  const [isOrderedList, setIsOrderedList] = useState(false)
  const [isUnorderedList, setIsUnorderedList] = useState(false)
  const [isQuote, setIsQuote] = useState(false)

  const updatePopup = useCallback(() => {
    editor.getEditorState().read(() => {
      // Should not to pop up the floating toolbar when using IME input
      if (editor.isComposing()) {
        return
      }
      const selection = $getSelection()
      const nativeSelection = window.getSelection()
      const rootElement = editor.getRootElement()

      if (
        nativeSelection !== null &&
        (!$isRangeSelection(selection) || rootElement === null || !rootElement.contains(nativeSelection.anchorNode))
      ) {
        setIsText(false)
        return
      }

      if (!$isRangeSelection(selection)) {
        return
      }

      const node = getSelectedNode(selection)

      // Update text format
      setIsBold(selection.hasFormat('bold'))
      setIsItalic(selection.hasFormat('italic'))
      setIsCode(selection.hasFormat('code'))
      setIsOrderedList(selection.hasFormat('orderedList' as TextFormatType))
      setIsUnorderedList(selection.hasFormat('unorderedList' as TextFormatType))
      setIsQuote(selection.hasFormat('quote' as TextFormatType))

      if (!$isCodeHighlightNode(selection.anchor.getNode()) && selection.getTextContent() !== '') {
        setIsText($isTextNode(node))
      } else {
        setIsText(false)
      }

      const rawTextContent = selection.getTextContent().replace(/\n/g, '')
      if (!selection.isCollapsed() && rawTextContent === '') {
        setIsText(false)
      }
    })
  }, [editor])

  useEffect(() => {
    document.addEventListener('selectionchange', updatePopup)
    return () => {
      document.removeEventListener('selectionchange', updatePopup)
    }
  }, [updatePopup])

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(() => {
          updatePopup()
        }),
        editor.registerRootListener(() => {
          if (editor.getRootElement() === null) {
            setIsText(false)
          }
        })
      ),
    [editor, updatePopup]
  )

  if (!isText) {
    return null
  }

  return createPortal(
    <TextFormatFloatingToolbar
      editor={editor}
      anchorElem={anchorElem}
      isBold={isBold}
      isItalic={isItalic}
      isCode={isCode}
      isOrderedList={isOrderedList}
      isUnorderedList={isUnorderedList}
      isQuote={isQuote}
    />,
    anchorElem
  )
}

export default function FloatingTextFormatToolbarPlugin({
  anchorElem = document.body
}: {
  anchorElem?: HTMLElement
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext()
  return useFloatingTextFormatToolbar(editor, anchorElem)
}
